import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Textarea } from "@/common/components/ui/textarea";
import { Label } from "@/common/components/ui/label";
import { Checkbox } from "@/common/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { Badge } from "@/common/components/ui/badge";
import {
  ArrowLeft,
  Save,
  Loader2,
  Plus,
  X,
  Upload,
  Package,
  Layers,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GoldService } from "@/services/APIs";
import { DropDownData } from "@/utils/types/Gold";
import AntDTable from "@/common/components/ui/antd-table";
import CommonModal from "@/common/components/ui/modal";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { notify } from "@/common/components/common/Notification";

interface ProductVariant {
  id?: string;
  sku: string;
  dimension: string;
  weight_grams: string;
  mrp: string;
  selling_price: string;
  current_stock: string;
  sold: string;
  description: string;
}

interface ProductImage {
  file: File | null;
  preview: string;
}

const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        const MAX_WIDTH = 1200;
        const MAX_HEIGHT = 1200;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          blob => {
            if (blob) {
              const newFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(newFile);
            } else {
              reject(new Error("Canvas to Blob failed"));
            }
          },
          "image/jpeg",
          0.7
        );
      };
      img.onerror = error => reject(error);
    };
    reader.onerror = error => reject(error);
  });
};

const AddProductPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [form, setForm] = useState({
    is_active: true,
    name: "",
    metal: "",
    purity: "",
    shape: "",
    hsn_code: "",
    brand_name: "",
    manufacturer_details: "",
    seller_details: "",
    country_of_origin: "India",
    gst_percentage: "",
    category: "Bullion",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [productImages, setProductImages] = useState<ProductImage[]>([
    { file: null, preview: "" },
    { file: null, preview: "" },
    { file: null, preview: "" },
    { file: null, preview: "" },
    { file: null, preview: "" },
    { file: null, preview: "" },
    { file: null, preview: "" },
    { file: null, preview: "" },
  ]);

  const [variants, setVariants] = useState<ProductVariant[]>([
    {
      sku: "",
      dimension: "",
      weight_grams: "",
      mrp: "",
      selling_price: "",
      current_stock: "0",
      sold: "0",
      description: "",
    },
  ]);

  // Stock Modal State
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [currentVariantIndex, setCurrentVariantIndex] = useState<number | null>(
    null
  );
  const [stockToAdd, setStockToAdd] = useState("");
  const [purityChoices, setPurityChoices] = useState<DropDownData[]>([]);
  const [metalChoices, setMetalChoices] = useState<DropDownData[]>([]);
  const [shapeChoices, setShapeChoices] = useState<DropDownData[]>([]);
  const [isLowStockModalOpen, setIsLowStockModalOpen] = useState(false);
  const [lowStockVariantsList, setLowStockVariantsList] = useState<any[]>([]);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        // 1. Fetch all dropdown options first
        const [purityRes, metalRes, shapeRes] = await Promise.all([
          GoldService.getPurityChoices(),
          GoldService.getMetalChoices(),
          GoldService.getShapeChoices(),
        ]);

        if (purityRes.success) {
          setPurityChoices(purityRes.data);
        }
        if (metalRes.success) {
          setMetalChoices(metalRes.data);
        }
        if (shapeRes.success) {
          setShapeChoices(shapeRes.data);
        }

        // 2. If in Edit mode, fetch product details
        if (id) {
          setIsEditMode(true);
          const response = await GoldService.getProductById(id);
          if (response.success && response.data) {
            const product = response.data;
            setForm(prev => ({
              ...prev,
              is_active: product.is_enabled,
              name: product.product_name,
              metal: product.metal,
              purity: product.purity,
              shape: product.shape,
              hsn_code: (product as any).hsn_code ?? "",
              brand_name: (product as any).brand_name ?? "",
              manufacturer_details: (product as any).manufacturer_details ?? "",
              seller_details: (product as any).seller_details ?? "",
              country_of_origin: product.country_of_origin,
              gst_percentage:
                product.gst_percentage !== null &&
                String(product.gst_percentage) !== ""
                  ? String(product.gst_percentage)
                  : "",
              category: (product as any).category ?? "Bullion",
            }));

            // Map variants
            const mappedVariants = product.variants.map((v: any) => ({
              id: v.id,
              sku: v.sku_code ?? v.sku ?? "",
              dimension: v.dimension,
              weight_grams: v.weight_grams,
              mrp: v.mrp,
              selling_price: v.selling_price,
              current_stock: String(v.current_stock),
              sold: String(v.sold),
              description: v.description ?? v.product_description ?? "",
            }));
            setVariants(mappedVariants);

            // Check for low stock on initialization (show summary modal instead of notify)
            const lowStock = mappedVariants.filter(
              (v: any) => parseInt(v.current_stock) < 10
            );
            if (lowStock.length > 0) {
              setLowStockVariantsList(lowStock);
              setIsLowStockModalOpen(true);
            }

            // Map images
            const serverImages = [
              product.main_image,
              product.image_2,
              product.image_3,
              product.image_4,
              product.image_5,
              product.image_6,
              product.image_7,
              product.image_8,
            ];

            const newImages = Array(8)
              .fill(null)
              .map(() => ({ file: null, preview: "" }));

            const baseUrl =
              import.meta.env.VITE_API_BASE_URL ??
              "https://thefineaseapi.universal-software.in";

            serverImages.forEach((imgUrl, index) => {
              if (imgUrl && index < newImages.length) {
                let fullUrl = String(imgUrl).trim();
                if (!fullUrl.startsWith("http")) {
                  const cleanBase = baseUrl.endsWith("/")
                    ? baseUrl.slice(0, -1)
                    : baseUrl;
                  const cleanPath = fullUrl.startsWith("/")
                    ? fullUrl
                    : `/${fullUrl}`;
                  fullUrl = `${cleanBase}${cleanPath}`;
                }
                newImages[index] = { file: null, preview: fullUrl };
              }
            });
            setProductImages(newImages);
          }
        } else {
          // 3. If in Create mode, set defaults from fetched options
          setForm(prev => ({
            ...prev,
            purity:
              purityRes.success && purityRes.data.length > 0
                ? purityRes.data[0].value
                : "",
            metal:
              metalRes.success && metalRes.data.length > 0
                ? metalRes.data[0].value
                : "Gold",
            shape:
              shapeRes.success && shapeRes.data.length > 0
                ? shapeRes.data[0].value
                : "",
          }));
        }
      } catch (error) {
        console.error("Initialization error:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to initialize page data",
        });
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [id, toast]);

  // Effect to handle state changes when Metal changes to Silver
  useEffect(() => {
    if (form.metal?.toLowerCase() === "silver") {
      setForm(prev => ({
        ...prev,
        shape: ["Cast Bar", "Coin", "Rectangular Ingot", "Pendants"].includes(
          prev.shape
        )
          ? prev.shape
          : "Coin",
        purity: "999.9+",
        category: prev.category || "Bullion",
      }));
    }
  }, [form.metal]);

  const handleImageUpload = async (index: number, file: File | null) => {
    if (file) {
      try {
        const compressedFile = await compressImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          const newImages = [...productImages];
          newImages[index] = {
            file: compressedFile,
            preview: reader.result as string,
          };
          setProductImages(newImages);
          if (index === 0 && errors.mainImage) {
            setErrors(prev => ({ ...prev, mainImage: "" }));
          }
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error("Image compression failed, using original file", error);
        // Fallback to original file if compression fails
        const reader = new FileReader();
        reader.onloadend = () => {
          const newImages = [...productImages];
          newImages[index] = {
            file: file,
            preview: reader.result as string,
          };
          setProductImages(newImages);
          if (index === 0 && errors.mainImage) {
            setErrors(prev => ({ ...prev, mainImage: "" }));
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...productImages];
    newImages[index] = { file: null, preview: "" };
    setProductImages(newImages);
  };

  const handleAddVariant = () => {
    setVariants([
      ...variants,
      {
        sku: "",
        dimension: "",
        weight_grams: "",
        mrp: "",
        selling_price: "",
        current_stock: "0",
        sold: "0",
        description: "",
      },
    ]);
  };

  const handleRemoveVariant = (index: number) => {
    if (variants.length > 1) {
      setVariants(variants.filter((_, i) => i !== index));
    }
  };

  const handleVariantChange = (
    index: number,
    field: keyof ProductVariant,
    value: string
  ) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setVariants(newVariants);

    // Clear variant-specific errors
    const errorKey = `variant_${index}_${field === "weight_grams" ? "weight" : field}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrs = { ...prev };
        delete newErrs[errorKey];
        return newErrs;
      });
    }
  };

  const openStockModal = (index: number) => {
    setCurrentVariantIndex(index);
    setStockToAdd("");
    setIsStockModalOpen(true);
  };

  const handleConfirmAddStock = () => {
    if (
      currentVariantIndex !== null &&
      stockToAdd &&
      !isNaN(Number(stockToAdd))
    ) {
      const newVariants = [...variants];
      const newStock =
        parseInt(newVariants[currentVariantIndex].current_stock || "0") +
        parseInt(stockToAdd);

      newVariants[currentVariantIndex].current_stock = newStock.toString();

      setVariants(newVariants);

      // Update low stock list & modal if needed
      const currentLowStock = newVariants.filter(
        v => parseInt(v.current_stock) < 10
      );
      if (currentLowStock.length > 0) {
        setLowStockVariantsList(currentLowStock);
        setIsLowStockModalOpen(true);
      }

      setIsStockModalOpen(false);
      setStockToAdd("");
      setCurrentVariantIndex(null);
    } else {
      toast({
        variant: "destructive",
        title: "Invalid Input",
        description: "Please enter a valid number for stock.",
      });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form
      const newErrors: Record<string, string> = {};
      if (!form.name.trim()) {
        newErrors.name = "Product name is required";
      }
      if (!form.metal) {
        newErrors.metal = "Metal is required";
      }
      if (!form.purity) {
        newErrors.purity = "Purity is required";
      }
      if (!form.shape) {
        newErrors.shape = "Shape is required";
      }
      if (!form.hsn_code.trim()) {
        newErrors.hsn_code = "HSN code is required";
      }
      if (!form.brand_name.trim()) {
        newErrors.brand_name = "Brand Name is required";
      }
      if (!form.manufacturer_details.trim()) {
        newErrors.manufacturer_details = "Manufacturer details are required";
      }
      if (!form.seller_details.trim()) {
        newErrors.seller_details = "Seller details are required";
      }
      if (!form.country_of_origin.trim()) {
        newErrors.country_of_origin = "Country of origin is required";
      }
      if (!form.gst_percentage.trim()) {
        newErrors.gst_percentage = "GST percentage is required";
      }

      if (!productImages[0].preview) {
        newErrors.mainImage = "Main image is required";
      }

      if (variants.length === 0) {
        newErrors.variants = "At least one variant is required";
      } else {
        variants.forEach((v, idx) => {
          if (!v.sku.trim()) {
            newErrors[`variant_${idx}_sku`] = "SKU is required";
          }
          if (!v.dimension.trim()) {
            newErrors[`variant_${idx}_dimension`] = "Dimension is required";
          }
          if (!v.weight_grams) {
            newErrors[`variant_${idx}_weight`] = "Weight is required";
          }
          if (!v.mrp) {
            newErrors[`variant_${idx}_mrp`] = "MRP is required";
          }
          if (!v.selling_price) {
            newErrors[`variant_${idx}_selling_price`] =
              "Selling price is required";
          }
        });
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Please fill in all required fields",
        });
        setLoading(false);
        return;
      }

      setErrors({});

      const formData = new FormData();
      formData.append("is_enabled", String(form.is_active));
      formData.append("product_name", form.name);
      formData.append("metal", form.metal);

      formData.append("purity", form.purity);

      formData.append("shape", form.shape);
      formData.append("hsn_code", form.hsn_code);
      formData.append("brand_name", form.brand_name);
      formData.append("manufacturer_details", form.manufacturer_details);
      formData.append("seller_details", form.seller_details);
      formData.append("country_of_origin", form.country_of_origin);
      const gstTrimmed = form.gst_percentage.trim();
      if (gstTrimmed !== "" && !Number.isNaN(Number(gstTrimmed))) {
        formData.append("gst_percentage", String(Number(gstTrimmed)));
      }
      if (form.metal?.toLowerCase() === "silver") {
        formData.append("category", form.category);
      }

      variants.forEach((v, index) => {
        formData.append(`variants[${index}]sku_code`, v.sku);
        formData.append(`variants[${index}]dimension`, v.dimension);
        formData.append(`variants[${index}]weight_grams`, v.weight_grams);
        formData.append(`variants[${index}]mrp`, v.mrp);
        formData.append(`variants[${index}]selling_price`, v.selling_price);
        formData.append(`variants[${index}]description`, v.description);
        formData.append(
          `variants[${index}]current_stock`,
          String(parseInt(v.current_stock || "0"))
        );
        formData.append(
          `variants[${index}]sold`,
          String(parseInt(v.sold || "0"))
        );
      });

      productImages.forEach((img, idx) => {
        if (img.file) {
          const key = idx === 0 ? "main_image" : `image_${idx + 1}`;
          formData.append(key, img.file);
        }
      });

      let response;
      if (isEditMode && id) {
        response = await GoldService.updateProduct(id, formData);
      } else {
        response = await GoldService.createProduct(formData);
      }
      if (response.success) {
        toast({
          title: "Success",
          description:
            response.message ||
            `Product ${isEditMode ? "updated" : "added"} successfully`,
          variant: "default",
        });
        navigate("/admin/physical-gold/product-master");
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description:
            response.message ||
            `Failed to ${isEditMode ? "update" : "add"} product`,
        });
      }
    } catch (error: any) {
      console.error("Add Product Error:", error);

      const errorMessage: any = "An unexpected error occurred";

      if (error?.response?.data) {
        const data = error.response.data;
        if (
          data.errors &&
          typeof data.errors === "object" &&
          Object.keys(data.errors).length > 0
        ) {
          notify.error(data.message);
        } else if (data.message) {
          notify.error(data.message);
        }
      } else if (error?.message) {
        notify.error(error.message);
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50">
      <form onSubmit={handleSave} className="flex h-full flex-col">
        {/* Header */}
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-7xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() =>
                    navigate("/admin/physical-gold/product-master")
                  }
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                      {isEditMode ? "Edit Product" : "Add New Product"}
                    </h1>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[12px] font-bold uppercase tracking-wider ${
                        form.is_active
                          ? "border-emerald-100 bg-emerald-50 text-emerald-600"
                          : "border-gray-100 bg-gray-50 text-gray-600"
                      }`}
                    >
                      {form.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    {isEditMode
                      ? "Update existing physical gold product details"
                      : "Create a new physical gold product with variants"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {isEditMode ? "Update Product" : "Save Product"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mx-auto w-full max-w-7xl flex-1 space-y-8 p-6">
          {/* Basic Details Card */}
          <Card className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
                <Package className="h-5 w-5 text-primary" />
                Product Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-x-8 gap-y-6 p-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Product Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  placeholder="e.g., 24K Purest Lotus Gold Coin"
                  value={form.name}
                  onChange={e => {
                    setForm({ ...form, name: e.target.value });
                    if (errors.name) {
                      setErrors(prev => ({ ...prev, name: "" }));
                    }
                  }}
                  className={`h-11 border-slate-200 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                    errors.name ? "border-red-500" : ""
                  }`}
                  required
                />
                {errors.name && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Metal <span className="text-red-500">*</span>
                </Label>
                <Select
                  key={`metal-${metalChoices.length}`}
                  value={form.metal}
                  onValueChange={v => setForm({ ...form, metal: v })}
                >
                  <SelectTrigger
                    className={`h-11 border-slate-200 focus:ring-primary/10 ${
                      errors.metal ? "border-red-500" : ""
                    }`}
                  >
                    <SelectValue placeholder="Select Metal" />
                  </SelectTrigger>
                  <SelectContent>
                    {metalChoices.map(choice => (
                      <SelectItem key={choice.value} value={choice.value}>
                        {choice.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.metal && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.metal}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Purity <span className="text-red-500">*</span>
                </Label>
                <Select
                  key={`purity-${purityChoices.length}-${form.metal}`}
                  value={form.purity}
                  onValueChange={v => setForm({ ...form, purity: v })}
                  disabled={form.metal?.toLowerCase() === "silver"}
                >
                  <SelectTrigger
                    className={`h-11 border-slate-200 focus:ring-primary/10 ${
                      errors.purity ? "border-red-500" : ""
                    }`}
                  >
                    <SelectValue placeholder="Select Purity" />
                  </SelectTrigger>
                  <SelectContent>
                    {form.metal?.toLowerCase() === "silver" ? (
                      <SelectItem value="999.9+">999.9+</SelectItem>
                    ) : (
                      purityChoices.map(choice => (
                        <SelectItem key={choice.value} value={choice.value}>
                          {choice.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.purity && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.purity}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Shape <span className="text-red-500">*</span>
                </Label>
                <Select
                  key={`shape-${shapeChoices.length}`}
                  value={form.shape}
                  onValueChange={v => setForm({ ...form, shape: v })}
                >
                  <SelectTrigger
                    className={`h-11 border-slate-200 focus:ring-primary/10 ${
                      errors.shape ? "border-red-500" : ""
                    }`}
                  >
                    <SelectValue placeholder="Select Shape" />
                  </SelectTrigger>
                  <SelectContent>
                    {shapeChoices.map(choice => (
                      <SelectItem key={choice.value} value={choice.value}>
                        {choice.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.shape && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.shape}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  HSN code <span className="text-red-500">*</span>
                </Label>
                <Input
                  placeholder="e.g., 7118"
                  value={form.hsn_code}
                  onChange={e => {
                    setForm({ ...form, hsn_code: e.target.value });
                    if (errors.hsn_code) {
                      setErrors(prev => ({ ...prev, hsn_code: "" }));
                    }
                  }}
                  className={`h-11 border-slate-200 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                    errors.hsn_code ? "border-red-500" : ""
                  }`}
                  required
                />
                {errors.hsn_code && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.hsn_code}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Brand Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  placeholder="e.g., MMTC-PAMP"
                  value={form.brand_name}
                  onChange={e => {
                    setForm({ ...form, brand_name: e.target.value });
                    if (errors.brand_name) {
                      setErrors(prev => ({ ...prev, brand_name: "" }));
                    }
                  }}
                  className={`h-11 border-slate-200 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                    errors.brand_name ? "border-red-500" : ""
                  }`}
                  required
                />
                {errors.brand_name && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.brand_name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Manufacturer Details <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  placeholder="e.g., Manufacturer Name, Address"
                  value={form.manufacturer_details}
                  onChange={e => {
                    setForm({ ...form, manufacturer_details: e.target.value });
                    if (errors.manufacturer_details) {
                      setErrors(prev => ({
                        ...prev,
                        manufacturer_details: "",
                      }));
                    }
                  }}
                  className={`min-h-[100px] border-slate-200 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                    errors.manufacturer_details ? "border-red-500" : ""
                  }`}
                  required
                />
                {errors.manufacturer_details && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.manufacturer_details}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Seller Details <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  placeholder="e.g., Seller Name, GSTIN"
                  value={form.seller_details}
                  onChange={e => {
                    setForm({ ...form, seller_details: e.target.value });
                    if (errors.seller_details) {
                      setErrors(prev => ({ ...prev, seller_details: "" }));
                    }
                  }}
                  className={`min-h-[100px] border-slate-200 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                    errors.seller_details ? "border-red-500" : ""
                  }`}
                  required
                />
                {errors.seller_details && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.seller_details}
                  </p>
                )}
              </div>
              {form.metal?.toLowerCase() === "silver" && (
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Category
                  </Label>
                  <Select
                    value={form.category}
                    onValueChange={v => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger className="h-11 border-slate-200 focus:ring-primary/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["Bullion", "Devotional", "Classic", "Gifting"].map(
                        choice => (
                          <SelectItem key={choice} value={choice}>
                            {choice}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Country of Origin <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={form.country_of_origin}
                  onChange={e => {
                    setForm({
                      ...form,
                      country_of_origin: e.target.value,
                    });
                    if (errors.country_of_origin) {
                      setErrors(prev => ({ ...prev, country_of_origin: "" }));
                    }
                  }}
                  className={`h-11 border-slate-200 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                    errors.country_of_origin ? "border-red-500" : ""
                  }`}
                />
                {errors.country_of_origin && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.country_of_origin}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  GST (%) <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  placeholder="e.g., 3"
                  value={form.gst_percentage}
                  onChange={e => {
                    setForm({ ...form, gst_percentage: e.target.value });
                    if (errors.gst_percentage) {
                      setErrors(prev => ({ ...prev, gst_percentage: "" }));
                    }
                  }}
                  className={`h-11 border-slate-200 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                    errors.gst_percentage ? "border-red-500" : ""
                  }`}
                />
                {errors.gst_percentage && (
                  <p className="text-[10px] font-medium text-red-500">
                    {errors.gst_percentage}
                  </p>
                )}
              </div>
              <div className="mt-4 flex items-center space-x-2">
                <Checkbox
                  id="is_active"
                  checked={form.is_active}
                  onCheckedChange={checked =>
                    setForm({ ...form, is_active: checked as boolean })
                  }
                  className="data-[state=checked]:bg-primary"
                />
                <Label
                  htmlFor="is_active"
                  className="cursor-pointer text-sm font-medium text-slate-700"
                >
                  Enable Product
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Images Section */}
          <Card className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
                <Upload className="h-5 w-5 text-primary" />
                Product Images <span className="text-red-500">*</span>
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  (First image will be main thumbnail)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {errors.mainImage && (
                <p className="mb-4 text-xs font-medium text-red-500">
                  {errors.mainImage}
                </p>
              )}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {productImages.map((img, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={idx} className="space-y-2">
                    <div className="relative">
                      {!img.preview ? (
                        <label className="flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 transition-colors hover:border-primary hover:bg-primary/5">
                          <Upload className="mb-1 h-6 w-6 text-slate-400" />
                          <span className="text-xs font-medium text-slate-500">
                            {idx === 0 ? "Main Image" : `Image ${idx + 1}`}
                          </span>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleImageUpload(idx, file);
                              }
                            }}
                          />
                        </label>
                      ) : (
                        <div className="group relative h-32 w-full overflow-hidden rounded-lg border border-slate-200">
                          <img
                            src={img.preview}
                            alt={`Product ${idx + 1}`}
                            title={img.preview} // Debug: Show URL on hover
                            onError={(e: any) =>
                              console.error(
                                `Failed to load image at index ${idx}:`,
                                img.preview,
                                e
                              )
                            }
                            className="h-full w-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/10" />
                          {idx === 0 && (
                            <Badge className="absolute left-2 top-2 bg-primary/90 text-[10px] font-bold shadow-sm backdrop-blur-sm">
                              MAIN
                            </Badge>
                          )}
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            className="absolute right-2 top-2 h-7 w-7 opacity-0 shadow-sm transition-all group-hover:opacity-100"
                            onClick={() => handleRemoveImage(idx)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {errors.variants && (
                <p className="mt-4 text-xs font-medium text-red-500">
                  {errors.variants}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Variants Section */}
          <Card className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
                  <Layers className="h-5 w-5 text-primary" />
                  Product Variants
                </CardTitle>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddVariant}
                  className="gap-2 bg-primary/10 text-primary hover:bg-primary/20"
                >
                  <Plus className="h-4 w-4" /> Add Variant
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-hidden">
                <AntDTable
                  columns={[
                    {
                      title: (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          SKU Code <span className="text-red-500">*</span>
                        </span>
                      ),
                      key: "sku",
                      className: "text-xs font-bold uppercase text-slate-500",
                      render: (_, variant, idx) => (
                        <Input
                          placeholder="e.g., G001"
                          value={variant.sku}
                          onChange={e =>
                            handleVariantChange(idx, "sku", e.target.value)
                          }
                          className={`h-9 border-slate-200 text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                            errors[`variant_${idx}_sku`] ? "border-red-500" : ""
                          }`}
                          required
                        />
                      ),
                    },
                    {
                      title: (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          Dimension <span className="text-red-500">*</span>
                        </span>
                      ),
                      key: "dimension",
                      className: "text-xs font-bold uppercase text-slate-500",
                      render: (_, variant, idx) => (
                        <Input
                          placeholder="Dia 11.0 x 0.3 mm"
                          value={variant.dimension}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "dimension",
                              e.target.value
                            )
                          }
                          className={`h-9 border-slate-200 text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                            errors[`variant_${idx}_dimension`]
                              ? "border-red-500"
                              : ""
                          }`}
                        />
                      ),
                    },
                    {
                      title: (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          Weight (Grams) <span className="text-red-500">*</span>
                        </span>
                      ),
                      key: "weight",
                      className: "text-xs font-bold uppercase text-slate-500",
                      render: (_, variant, idx) => (
                        <Input
                          type="number"
                          step="0.001"
                          placeholder="0.5"
                          value={variant.weight_grams}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "weight_grams",
                              e.target.value
                            )
                          }
                          className={`h-9 border-slate-200 text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                            errors[`variant_${idx}_weight`]
                              ? "border-red-500"
                              : ""
                          }`}
                        />
                      ),
                    },
                    {
                      title: (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          MRP (₹) <span className="text-red-500">*</span>
                        </span>
                      ),
                      key: "mrp",
                      className: "text-xs font-bold uppercase text-slate-500",
                      render: (_, variant, idx) => (
                        <Input
                          type="number"
                          placeholder="10390"
                          value={variant.mrp}
                          onChange={e =>
                            handleVariantChange(idx, "mrp", e.target.value)
                          }
                          className={`h-9 border-slate-200 text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                            errors[`variant_${idx}_mrp`] ? "border-red-500" : ""
                          }`}
                        />
                      ),
                    },
                    {
                      title: (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          Selling Price (₹){" "}
                          <span className="text-red-500">*</span>
                        </span>
                      ),
                      key: "selling",
                      className: "text-xs font-bold uppercase text-slate-500",
                      render: (_, variant, idx) => (
                        <Input
                          type="number"
                          placeholder="9030"
                          value={variant.selling_price}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "selling_price",
                              e.target.value
                            )
                          }
                          className={`h-9 border-slate-200 text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${
                            errors[`variant_${idx}_selling_price`]
                              ? "border-red-500"
                              : ""
                          }`}
                        />
                      ),
                    },
                    {
                      title: "Description",
                      key: "description",
                      className: "text-xs font-bold uppercase text-slate-500",
                      width: 200,
                      render: (_, variant, idx) => (
                        <Input
                          placeholder="Variant description"
                          value={variant.description}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "description",
                              e.target.value
                            )
                          }
                          className="h-9 border-slate-200 text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10"
                        />
                      ),
                    },
                    {
                      title: (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          Current Stock <span className="text-red-500">*</span>
                        </span>
                      ),
                      key: "stock",
                      className: "text-xs font-bold uppercase text-slate-500",
                      render: (_, variant, idx) => (
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={variant.current_stock}
                            onChange={e =>
                              handleVariantChange(
                                idx,
                                "current_stock",
                                e.target.value
                              )
                            }
                            className="h-9 w-20 border-slate-200 text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10"
                            readOnly
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={() => openStockModal(idx)}
                            className="h-9 w-9 border-slate-200 hover:bg-slate-50"
                          >
                            <Plus className="h-4 w-4 text-slate-600" />
                          </Button>
                        </div>
                      ),
                    },
                    {
                      title: "Sold",
                      key: "sold",
                      className: "text-xs font-bold uppercase text-slate-500",
                      render: (_, variant, idx) => (
                        <Input
                          type="number"
                          value={variant.sold}
                          onChange={e =>
                            handleVariantChange(idx, "sold", e.target.value)
                          }
                          className="h-9 border-slate-200 text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10"
                        />
                      ),
                    },
                    {
                      title: "",
                      key: "action",
                      width: 50,
                      render: (_, __, idx) =>
                        variants.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveVariant(idx)}
                            className="h-9 w-9 text-slate-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        ),
                    },
                  ]}
                  data={variants}
                  scroll={{}}
                  pagination={false}
                  rowKey={(_, index) =>
                    index?.toString() ?? Math.random().toString()
                  }
                />
              </div>
              {Object.keys(errors).some(k => k.startsWith("variant_")) && (
                <p className="border-t p-3 text-xs font-medium text-red-500">
                  Please fill SKU Code, Dimension, Weight (Grams), MRP (₹) and
                  Selling Price (₹) for all variants.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </form>

      <CommonModal
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        width="w-[400px]"
        padding="p-6"
      >
        <div className="flex flex-col gap-4">
          <div className="border-b pb-4">
            <h3 className="text-lg font-bold text-slate-800">Update Stock</h3>
          </div>

          <div className="space-y-4 py-2 font-sans">
            <Label className="text-sm font-normal">
              Quantity (Add/Subtract)
            </Label>
            <Input
              type="number"
              placeholder="Enter quantity (e.g., 10 or -5)"
              value={stockToAdd}
              onChange={e => setStockToAdd(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Positive value to add stock, negative to reduce.
            </p>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4 font-sans">
            <Button
              variant="outline"
              onClick={() => setIsStockModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmAddStock} className="gap-2 px-8">
              Update
            </Button>
          </div>
        </div>
      </CommonModal>

      <CommonModal
        isOpen={isLowStockModalOpen}
        onClose={() => setIsLowStockModalOpen(false)}
        width="w-[500px]"
        padding="p-6"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 border-b pb-4 text-amber-600">
            <Package className="h-5 w-5" />
            <h3 className="text-lg font-bold">Low Stock Alert</h3>
          </div>

          <div className="space-y-4 py-2 font-sans">
            <p className="text-sm text-slate-600">
              The following variants have stock levels below the threshold (10
              units):
            </p>
            <div className="max-h-[300px] overflow-auto rounded-lg border border-slate-100">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">SKU</th>
                    <th className="px-4 py-3">Weight</th>
                    <th className="px-4 py-3 text-right">Stock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {lowStockVariantsList.map((v, idx) => (
                    <tr
                      key={v.sku ?? idx}
                      className="transition-colors hover:bg-amber-50/30"
                    >
                      <td className="px-4 py-3 font-medium text-slate-700">
                        {v.sku ?? "N/A"}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {v.weight_grams} gm
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Badge
                          variant="outline"
                          className="border-amber-200 bg-amber-50 font-bold text-amber-700"
                        >
                          {v.current_stock}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end border-t pt-4">
            <Button
              onClick={() => setIsLowStockModalOpen(false)}
              className="bg-amber-600 font-bold text-white hover:bg-amber-700"
            >
              Acknowledged
            </Button>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default AddProductPage;
